import { Transition }               from '@uirouter/core';

import { ApiService }     from '../../app-lite/api/api.service';
import { CompanyService } from '../../app-lite/companies/company.service';
import { TableService }   from '../../app-lite/table/table.service';

import { CustomerActionsComponent } from './customer.actions.component';
import { CustomerComponent }       from '../../app-lite/customers/customer.component';
import { CustomersListComponent }  from '../../app-lite/customers/customers.list.component';

export const CustomersStates : Array<any> = [
  {
    component: CustomersListComponent,
    name     : 'app.customers',
    url      : '/customers',
    resolve    : [
      {
        token    : 'companyMessages',
        deps     : [ CompanyService ],
        resolveFn: ( companyService : CompanyService ) => companyService.getDefaultMessages()
      },
      {
        token    : 'customers',
        deps     : [ TableService, ApiService ],
        resolveFn: ( tableService : TableService, apiService : ApiService ) : Promise<any> => {
          const cache = tableService.getCacheForTable('customers');

          return apiService.stream('/filters', [
            cache.filtering.value ? cache.filtering.value : '*',
            'customers',
            0,
            cache.paging.pageSize   || 10,
            cache.sorting.field     || 'display_name',
            cache.sorting.direction || 'asc',
            !!cache.filtering.archived_only
          ]);
        }
      },
      {
        token    : 'actionsFactory',
        resolveFn: () => CustomerActionsComponent
      }
    ]
  },
  {
    component  : CustomerComponent,
    name       : 'app.customer',
    params     : {
      id: { squash: true, value: null }
    },
    url        : '/customer/:id',
    resolve    : [
      {
        token    : 'ams_autoclubs',
        deps     : [ ApiService ],
        resolveFn: ( apiService : ApiService ) : Promise<any> => apiService.get('/system/ams_autoclubs')
      },
      {
        token    : 'company',
        deps     : [ ApiService ],
        resolveFn: ( apiService : ApiService ) : Promise<any> => apiService.get('/company')
      },
      {
        token    : 'companyMessages',
        deps     : [ CompanyService ],
        resolveFn: ( companyService : CompanyService ) => companyService.getDefaultMessages()
      },
      {
        token    : 'contactMethods',
        deps     : [ ApiService ],
        resolveFn: ( apiService : ApiService ) : Promise<any> => apiService.get('/system/preferred_contact_method_types', null, 'contactMethods')
      },
      {
        token    : 'customerSources',
        deps     : [ ApiService ],
        resolveFn: ( apiService : ApiService ) : Promise<any> => apiService.get('/customer_sources', null, 'customerSources')
      },
      {
        token    : 'paymentTerms',
        deps     : [ ApiService ],
        resolveFn: ( apiService : ApiService ) : Promise<any> => apiService.get('system/payment_term_types', null, 'paymentTerms')
      },
      {
        token    : 'priceTypes',
        deps     : [ ApiService ],
        resolveFn: ( apiService : ApiService ) : Promise<any> => apiService.get('/system/customer_price_types', null, 'customerPriceTypes')
      },
      {
        token    : 'states',
        deps     : [ ApiService ],
        resolveFn: ( apiService : ApiService ) : Promise<any> => apiService.get('/system/state_types', null, 'states')
      },
      {
        token    : 'statusTypes',
        deps     : [ ApiService ],
        resolveFn: ( apiService : ApiService ) : Promise<any> => apiService.get('/system/invoice_status_types')
      },
      {
        token    : 'taxGroups',
        deps     : [ ApiService ],
        resolveFn: ( apiService : ApiService ) : Promise<any> => apiService.get('/tax_groups')
      },
      {
        token    : 'billedCustomers',
        deps     : [ Transition, TableService, ApiService ],
        resolveFn: ( transition : Transition, tableService : TableService, apiService : ApiService ) : Promise<any> | Array<any> =>
          transition.params().id
            ? apiService.get('/customers/biller_child_list', [
              transition.params().id,
              0,
              tableService.getCacheForTable('billedCustomers').paging.pageSize || 5
            ]) : []
      },
      {
        token    : 'biller_balance',
        deps     : [ Transition, ApiService ],
        resolveFn: ( transition : Transition, apiService : ApiService ) : Promise<any> | string =>
          transition.params().id
            ? apiService.get('/customer/biller_account_balance', transition.params().id)
            : ''
      },
      {
        token    : 'biller_credit_balance',
        deps     : [ Transition, ApiService ],
        resolveFn: ( transition : Transition, apiService : ApiService ) : Promise<any> | string =>
          transition.params().id
            ? apiService.get('/customer/biller_credit_balance', transition.params().id)
            : ''
      },
      {
        token    : 'customer',
        deps     : [ Transition, ApiService ],
        resolveFn: ( transition : Transition, apiService : ApiService ) : Promise<any> | string =>
          transition.params().id
            ? apiService.get('/customer', transition.params().id)
            : ''
      },
      {
        token    : 'events',
        deps     : [ Transition, TableService, ApiService ],
        resolveFn: ( transition : Transition, tableService : TableService, apiService : ApiService ) : Promise<any> | Array<any> => {
          const cache = tableService.getCacheForTable('customerEvents');
          const id    = transition.params().id;

          return id ? apiService.get('/customers/event_list', [
            id,
            0,
            cache.paging.pageSize   || 5,
            cache.sorting.field     || 'event_date',
            cache.sorting.direction || 'desc'
          ]) : [];
        }
      },
      {
        token    : 'inspections',
        deps     : [ Transition, TableService, ApiService ],
        resolveFn: ( transition : Transition, tableService : TableService, apiService : ApiService ) : Promise<any> | Array<any> => {
          const cache = tableService.getCacheForTable('customerInspections');
          const id    = transition.params().id;

          return id ? apiService.get('/customers/inspection_list', [
            id,
            0,
            cache.paging.pageSize   || 5,
            cache.sorting.field     || 'inspection_date',
            cache.sorting.direction || 'asc'
          ]) : [];
        }
      },
      {
        token    : 'invoices',
        deps     : [ Transition, TableService, ApiService ],
        resolveFn: ( transition : Transition, tableService : TableService, apiService : ApiService ) : Promise<any> | Array<any> => {
          const cache = tableService.getCacheForTable('customerInvoices');
          const id    = transition.params().id;

          return id ? apiService.get('/customers/invoice_list_with_find', [
            id,
            0,
            cache.paging.pageSize   || 5,
            cache.filtering.status_type ? cache.filtering.status_type : '*',
            '*',
            cache.filtering.begin_date  ? cache.filtering.begin_date  : '*',
            cache.filtering.end_date    ? cache.filtering.end_date    : '*',
            cache.sorting.field     || 'post_date',
            cache.sorting.direction || 'desc',
            cache.filtering.reference   ? cache.filtering.reference   : '*'
          ]) : [];
        }
      },
      {
        token    : 'payments',
        deps     : [ Transition, TableService, ApiService ],
        resolveFn: ( transition : Transition, tableService : TableService, apiService : ApiService ) : Promise<any> | Array<any> => {
          const cache = tableService.getCacheForTable('customerPayments');
          const id    = transition.params().id;

          return id ? apiService.get('/customers/payment_list', [
            id,
            0,
            cache.paging.pageSize   || 5,
            cache.sorting.field     || 'post_date',
            cache.sorting.direction || 'asc'
          ]) : [];
        }
      },
      {
        token    : 'quotes',
        deps     : [ Transition, TableService, ApiService ],
        resolveFn: ( transition : Transition, tableService : TableService, apiService : ApiService ) : Promise<any> | Array<any> => {
          const cache = tableService.getCacheForTable('customerQuotes');
          const id    = transition.params().id;

          return id ? apiService.get('/customers/quote_list_with_find', [
            id,
            0,
            cache.paging.pageSize   || 5,
            cache.filtering.begin_date  ? cache.filtering.begin_date  : '*',
            cache.filtering.end_date    ? cache.filtering.end_date    : '*',
            cache.sorting.field     || 'post_date',
            cache.sorting.direction || 'desc',
            cache.filtering.reference   ? cache.filtering.reference   : '*'
          ]) : [];
        }
      },
      {
        token    : 'vehicles',
        deps     : [ Transition, TableService, ApiService ],
        resolveFn: ( transition : Transition, tableService : TableService, apiService : ApiService ) : Promise<any> | Array<any> => {
          const cache = tableService.getCacheForTable('customerVehicles');
          const id    = transition.params().id;

          return id ? apiService.get('/customers/vehicle_list', [
            id,
            0,
            cache.paging.pageSize   || 5,
            cache.sorting.field     || 'plate_number',
            cache.sorting.direction || 'asc'
          ]) : [];
        }
      }
    ]
  }
];
